import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./../../../assets/css/admin/gestionCategorie/AdminCategoriesPage.css";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import useCategorie from "../../../hooks/useCategorie.js";
import useGroupeCategorie from "../../../hooks/useGroupeCategorie.js";

export default function CategorieListePage({ setAlert }) {
  const { getAllCategories, updateCategorie, patchManyByGroupe } =
    useCategorie();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const [filters, setFilters] = useState({
    q: "",
    archive: "all",
    sort: "rang",
    groupeId: "all",
    premium: "all",
    hasPrice: "all",
  });

  const [groupeCategorie, setGroupeCategorie] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  // 🔥 État du modal de modification groupée
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    prixAbonnement: { enabled: false, value: 0 },
    prixBoost: { enabled: false, value: 0 },
    premium: { enabled: false, value: "2" },
    hasPrice: { enabled: false, value: "1" },
  });

  const breadcrumbItems = [
    { label: "Mon Compte", link: "/utilisateur/compte" },
    { label: "Catégories" },
  ];

  const { getAllGroupCategories } = useGroupeCategorie();

  const normalized = (v) =>
    String(v ?? "")
      .toLowerCase()
      .trim();

  const archiveLabel = (v) => (Number(v) === 1 ? "Archivé" : "Actif");
  const archiveBadgeClass = (v) =>
    Number(v) === 1 ? "badgeArchived" : "badgeActive";

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await getAllCategories();
      const list = res?.data?.data ?? res?.data ?? [];
      setCategories(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setCategories([]);
      setAlert?.({
        type: "danger",
        message: "Impossible de récupérer la liste des catégories.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
    getAllGroupCategories()
      .then((res) => {
        const list = res?.data?.data ?? res?.data ?? [];
        setGroupeCategorie(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        console.error(e);
        setAlert?.({
          type: "danger",
          message:
            "Impossible de récupérer la liste des groupes de catégories.",
        });
        setGroupeCategorie([]);
      });
  }, []);

  const filtered = useMemo(() => {
    let arr = Array.isArray(categories) ? [...categories] : [];
    if (filters.q.trim()) {
      const nq = normalized(filters.q);
      arr = arr.filter((c) => {
        const nom = normalized(c?.nom);
        const grp = normalized(c?.idCategorieGroupe?.nom);
        return nom.includes(nq) || grp.includes(nq);
      });
    }

    if (filters.archive !== "all") {
      arr = arr.filter((c) => String(c?.archive) === String(filters.archive));
    }

    if (filters.premium !== "all") {
      arr = arr.filter((c) => String(c?.premium) === String(filters.premium));
    }

    if (filters.groupeId !== "all") {
      arr = arr.filter(
        (c) => String(c?.idCategorieGroupe?._id) === String(filters.groupeId),
      );
    }

    if (filters.hasPrice !== "all") {
      arr = arr.filter((c) => String(c?.hasPrice) === String(filters.hasPrice));
    }

    arr.sort((a, b) => {
      const aNom = normalized(a?.nom);
      const bNom = normalized(b?.nom);
      const aR = Number(a?.rang ?? 0);
      const bR = Number(b?.rang ?? 0);

      switch (filters.sort) {
        case "-rang":
          return bR - aR;
        case "nom":
          return aNom.localeCompare(bNom, "fr", { sensitivity: "base" });
        case "-nom":
          return bNom.localeCompare(aNom, "fr", { sensitivity: "base" });
        case "rang":
        default:
          return aR - bR;
      }
    });

    return arr;
  }, [categories, filters]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const totalPages = Math.max(
    1,
    Math.ceil((filtered?.length || 0) / PAGE_SIZE),
  );
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  const handleToggleArchive = async (c) => {
    try {
      const id = c?._id;
      if (!id) return;

      const current = Number(c?.archive ?? 2);
      const next = current === 1 ? 2 : 1;

      await updateCategorie(id, { archive: next });

      setCategories((prev) =>
        (prev || []).map((x) =>
          String(x._id) === String(id) ? { ...x, archive: next } : x,
        ),
      );

      setAlert?.({
        type: "success",
        message: next === 1 ? "Catégorie archivée." : "Catégorie désarchivée.",
      });
    } catch (e) {
      console.error(e);
      setAlert?.({
        type: "danger",
        message: "Action impossible pour le moment.",
      });
    }
  };

  const lastRang = useMemo(() => {
    if (!Array.isArray(categories) || categories.length === 0) return 0;

    return categories.reduce((max, c) => {
      const r = Number(c?.rang ?? 0);
      return r > max ? r : max;
    }, 0);
  }, [categories]);

  const nextRang = lastRang + 1;

  const typeLabel = (t) => (Number(t) == 1 ? "Premium" : "Standard");
  const hasPriceLabel = (t) => (Number(t) == 1 ? "Oui" : "Non");

  // 🔥 Catégories concernées par le groupe sélectionné (sans les autres filtres)
  const categoriesDuGroupeSelectionne = useMemo(() => {
    if (filters.groupeId === "all") return [];
    return (categories || []).filter(
      (c) => String(c?.idCategorieGroupe?._id) === String(filters.groupeId),
    );
  }, [categories, filters.groupeId]);

  const nomGroupeSelectionne = useMemo(() => {
    if (filters.groupeId === "all") return "";
    return (
      groupeCategorie?.find((g) => String(g._id) === String(filters.groupeId))
        ?.nom || ""
    );
  }, [groupeCategorie, filters.groupeId]);

  const openBulkModal = () => {
    setBulkForm({
      prixAbonnement: {
        enabled: false,
        value: valeursActuellesGroupe.prixAbonnement.value ?? 0,
      },
      prixBoost: {
        enabled: false,
        value: valeursActuellesGroupe.prixBoost.value ?? 0,
      },
      premium: {
        enabled: false,
        value: String(valeursActuellesGroupe.premium.value ?? "2"),
      },
      hasPrice: {
        enabled: false,
        value: String(valeursActuellesGroupe.hasPrice.value ?? "1"),
      },
    });
    setShowBulkModal(true);
  };

  const closeBulkModal = () => {
    if (bulkSaving) return; // on ne ferme pas pendant l'enregistrement
    setShowBulkModal(false);
  };

  const updateBulkField = (key, patch) => {
    setBulkForm((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...patch },
    }));
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();

    const updates = {};
    if (bulkForm.prixAbonnement.enabled) {
      updates.prixAbonnement = Number(bulkForm.prixAbonnement.value) || 0;
    }
    if (bulkForm.prixBoost.enabled) {
      updates.prixBoost = Number(bulkForm.prixBoost.value) || 0;
    }
    if (bulkForm.premium.enabled) {
      updates.premium = Number(bulkForm.premium.value);
    }
    if (bulkForm.hasPrice.enabled) {
      updates.hasPrice = Number(bulkForm.hasPrice.value);
    }

    if (Object.keys(updates).length === 0) {
      setAlert?.({
        type: "warning",
        message: "Sélectionnez au moins un champ à modifier.",
      });
      return;
    }

    setBulkSaving(true);
    try {
      await patchManyByGroupe(filters.groupeId, updates);
      await loadCategories();
      setAlert?.({
        type: "success",
        message: `${categoriesDuGroupeSelectionne.length} catégorie(s) mise(s) à jour.`,
      });
      setShowBulkModal(false);
    } catch (err) {
      console.error(err);
      setAlert?.({
        type: "danger",
        message: "La modification groupée a échoué.",
      });
    } finally {
      setBulkSaving(false);
    }
  };

  // 🔥 Valeurs actuelles du groupe (pour pré-remplir le modal)
  const valeursActuellesGroupe = useMemo(() => {
    const getCommonValue = (field) => {
      if (categoriesDuGroupeSelectionne.length === 0) {
        return { value: null, mixed: false };
      }
      const first = categoriesDuGroupeSelectionne[0]?.[field];
      const allSame = categoriesDuGroupeSelectionne.every(
        (c) => String(c?.[field] ?? "") === String(first ?? ""),
      );
      return { value: allSame ? first : null, mixed: !allSame };
    };

    return {
      prixAbonnement: getCommonValue("prixAbonnement"),
      prixBoost: getCommonValue("prixBoost"),
      premium: getCommonValue("premium"),
      hasPrice: getCommonValue("hasPrice"),
    };
  }, [categoriesDuGroupeSelectionne]);

  return (
    <div className="adminCategoriesPage">
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="adminCategoriesGrid">
          <section className="adminCategoriesCard adminCategoriesCardCenter">
            {/* Header */}
            <div className="adminCategoriesHeader">
              <div className="adminCategoriesHeaderLeft">
                <div className="adminCategoriesTitle">
                  Gestion des catégories
                </div>
                <div className="adminCategoriesSubtitle">
                  {loading
                    ? "Chargement..."
                    : `${filtered.length} catégorie(s)`}
                </div>
              </div>

              <div className="adminCategoriesHeaderRight">
                <button
                  type="button"
                  className="adminCategoriesBtn adminCategoriesBtnSoft"
                  onClick={loadCategories}
                  title="Rafraîchir"
                  disabled={loading}
                >
                  <i className="bi bi-arrow-clockwise" />
                  <span>Rafraîchir</span>
                </button>

                <Link
                  to="/admin/categories/creation"
                  state={{
                    suggestedRang: nextRang,
                    from: location.pathname + location.search,
                  }}
                  className="adminCategoriesBtn adminCategoriesBtnPrimary"
                  title="Créer"
                >
                  <i className="bi bi-plus-lg" />
                  <span>Nouveau</span>
                </Link>
              </div>
            </div>

            {/* Filters */}
            <div className="adminCategoriesFilters">
              <div className="adminCategoriesSearch">
                <i className="bi bi-search" aria-hidden="true" />
                <input
                  type="text"
                  value={filters.q}
                  onChange={(e) => updateFilter("q", e.target.value)}
                  placeholder="Rechercher : catégorie, groupe..."
                />
              </div>

              <div className="adminCategoriesSelects">
                <select
                  value={filters.groupeId}
                  onChange={(e) => updateFilter("groupeId", e.target.value)}
                >
                  <option value="all">Tous les groupes</option>
                  {groupeCategorie?.map((g) => (
                    <option key={g._id} value={g._id}>
                      {g.nom}
                    </option>
                  ))}
                </select>

                <select
                  value={filters.premium}
                  onChange={(e) => updateFilter("premium", e.target.value)}
                >
                  <option value="all">Tous types</option>
                  <option value="1">Premium</option>
                  <option value="2">Standard</option>
                </select>

                <select
                  value={filters.archive}
                  onChange={(e) => updateFilter("archive", e.target.value)}
                >
                  <option value="all">Archive</option>
                  <option value="2">Actifs</option>
                  <option value="1">Archivés</option>
                </select>

                <select
                  value={filters.hasPrice}
                  onChange={(e) => updateFilter("hasPrice", e.target.value)}
                >
                  <option value="all">Tous les prix</option>
                  <option value="1">Oui</option>
                  <option value="2">Non</option>
                </select>

                <select
                  value={filters.sort}
                  onChange={(e) => updateFilter("sort", e.target.value)}
                >
                  <option value="rang">Rang ↑</option>
                  <option value="-rang">Rang ↓</option>
                  <option value="nom">Nom A → Z</option>
                  <option value="-nom">Nom Z → A</option>
                </select>

                {/* 🔥 Bouton visible uniquement si un groupe est sélectionné */}
                {filters.groupeId !== "all" && (
                  <button
                    type="button"
                    className="adminCategoriesBtn adminCategoriesBtnPrimary"
                    onClick={openBulkModal}
                    title="Modifier en masse les catégories de ce groupe"
                  >
                    <i className="bi bi-pencil-square" />
                    <span>
                      Modifier le groupe ({categoriesDuGroupeSelectionne.length}
                      )
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* Body */}
            {loading ? (
              <div className="adminCategoriesEmpty">
                <div className="adminCategoriesSpinner" />
                <div>Chargement des catégories...</div>
              </div>
            ) : paged.length === 0 ? (
              <div className="adminCategoriesEmpty">
                <i className="bi bi-box-seam" />
                <div>Aucune catégorie trouvée.</div>
              </div>
            ) : (
              <div className="adminCategoriesTableWrap">
                <table className="adminCategoriesTable">
                  <thead>
                    <tr>
                      <th>Catégorie</th>
                      <th className="hideOnMobile">Groupe</th>
                      <th className="hideOnMobile">Type</th>
                      <th className="hideOnMobile">Necessite Prix</th>
                      <th className="hideOnMobile">Champs</th>
                      <th className="textCenter">Prix abonnement</th>
                      <th className="textCenter">Prix boost</th>
                      <th className="textCenter">Statut</th>
                      <th className="colActions">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paged.map((c) => {
                      const nom = c?.nom || "—";
                      const groupe = c?.idCategorieGroupe?.nom || "—";
                      const fields = Array.isArray(c?.fields) ? c.fields : [];
                      const nbFields = fields.length;
                      const isArchived = Number(c?.archive) === 1;
                      const typeCategorie = Number(c?.premium ?? 2);

                      return (
                        <tr key={c?._id}>
                          <td>
                            <div className="adminCategoriesCellItem">
                              <div className="adminCategoriesThumb">
                                <i className="bi bi-tag" />
                              </div>

                              <div className="adminCategoriesCellItemText">
                                <div className="adminCategoriesName">{nom}</div>

                                <div className="adminCategoriesMobileMeta">
                                  <div>
                                    <i className="bi bi-diagram-3" /> {groupe}
                                  </div>
                                  <div>
                                    <i className="bi bi-list-check" />{" "}
                                    {nbFields} champ(s)
                                  </div>

                                  {nbFields > 0 && (
                                    <div className="adminCategoriesMobileFields">
                                      {fields.slice(0, 3).map((f, i) => (
                                        <span
                                          className="adminCategoriesChip"
                                          key={i}
                                        >
                                          {f?.name || "—"}
                                        </span>
                                      ))}
                                      {nbFields > 3 && (
                                        <span className="adminCategoriesChip muted">
                                          +{nbFields - 3}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="muted hideOnMobile">{groupe}</td>

                          <td className="muted hideOnMobile">
                            <span
                              className={`pill ${typeCategorie === 1 ? "pillPremium" : "pillStandard"}`}
                            >
                              {typeLabel(typeCategorie)}
                            </span>
                          </td>

                          <td className="muted hideOnMobile">
                            {hasPriceLabel(c?.hasPrice)}
                          </td>

                          <td className="muted hideOnMobile">
                            {nbFields > 0 ? (
                              <ul className="adminCategoriesFieldsList">
                                {fields.map((f, i) => (
                                  <li key={i}>
                                    {f?.icon ? (
                                      <i className={f.icon} />
                                    ) : (
                                      <i className="bi bi-dot" />
                                    )}
                                    <span>{f?.name || "—"}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <em>Aucun</em>
                            )}
                          </td>

                          <td className="muted hideOnMobile">
                            {c?.prixAbonnement ?? 0} Ar
                          </td>

                          <td className="muted hideOnMobile">
                            {c?.prixBoost ?? 0} Ar
                          </td>

                          <td className="textCenter">
                            <span
                              className={`adminCategoriesBadge ${archiveBadgeClass(c?.archive)}`}
                            >
                              {archiveLabel(c?.archive)}
                            </span>
                          </td>

                          <td className="colActions">
                            <div className="adminCategoriesActions">
                              <Link
                                to={`/admin/categories/modification/${c?._id}`}
                                state={{
                                  from: location.pathname + location.search,
                                }}
                                className="adminCategoriesIconBtn"
                                title="Modifier"
                              >
                                <i className="bi bi-pencil-square" />
                              </Link>

                              <button
                                type="button"
                                className="adminCategoriesIconBtn"
                                title={isArchived ? "Désarchiver" : "Archiver"}
                                onClick={() => handleToggleArchive(c)}
                              >
                                <i
                                  className={
                                    isArchived
                                      ? "bi bi-arrow-counterclockwise"
                                      : "bi bi-archive"
                                  }
                                />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && filtered.length > 0 && (
              <div className="adminCategoriesPagination">
                <button
                  type="button"
                  className="adminCategoriesPageBtn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                >
                  <i className="bi bi-chevron-left" />
                </button>

                <div className="adminCategoriesPageInfo">
                  Page <b>{safePage}</b> / {totalPages}
                </div>

                <button
                  type="button"
                  className="adminCategoriesPageBtn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
            )}
          </section>
        </div>
      </div>

      {/* 🔥 Modal Bootstrap - modification groupée */}
      {showBulkModal && (
        <>
          <div className="bulkModalBackdrop" />

          <div className="bulkModalOverlay" tabIndex="-1" role="dialog">
            <div
              className="bulkModalDialog"
              role="document"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bulkModalContent">
                <form onSubmit={handleBulkSubmit} className="bulkModalForm">
                  <div className="bulkModalHeader">
                    <div className="bulkModalHeaderIcon">
                      <i className="bi bi-pencil-square" />
                    </div>
                    <div className="bulkModalHeaderText">
                      <h5 className="bulkModalTitle">Modification groupée</h5>
                      <span className="bulkModalSubtitle">
                        {nomGroupeSelectionne}
                      </span>
                    </div>
                  </div>

                  <div className="bulkModalBody">
                    <div className="bulkModalInfo">
                      <i className="bi bi-info-circle" />
                      <p>
                        Cette action modifiera{" "}
                        <b>{categoriesDuGroupeSelectionne.length}</b>{" "}
                        catégorie(s) du groupe <b>{nomGroupeSelectionne}</b>.
                        Cochez uniquement les champs à modifier.
                      </p>
                    </div>

                    <div className="bulkModalFieldsGrid">
                      {/* Prix Abonnement */}
                      <div
                        className={`bulkModalField ${bulkForm.prixAbonnement.enabled ? "isActive" : ""}`}
                      >
                        <label
                          className="bulkModalCheck"
                          htmlFor="chkPrixAbonnement"
                        >
                          <input
                            type="checkbox"
                            id="chkPrixAbonnement"
                            checked={bulkForm.prixAbonnement.enabled}
                            onChange={(e) =>
                              updateBulkField("prixAbonnement", {
                                enabled: e.target.checked,
                              })
                            }
                          />
                          <span className="bulkModalCheckmark" />
                          <span className="bulkModalFieldLabel">
                            <i className="bi bi-cash-coin" />
                            Prix Abonnement
                          </span>
                        </label>

                        {valeursActuellesGroupe.prixAbonnement.mixed ? (
                          <span className="bulkModalCurrentHint mixed">
                            <i className="bi bi-exclamation-triangle" /> Valeurs
                            différentes
                          </span>
                        ) : (
                          <span className="bulkModalCurrentHint">
                            Actuel :{" "}
                            <b>
                              {valeursActuellesGroupe.prixAbonnement.value ?? 0}{" "}
                              Ar
                            </b>
                          </span>
                        )}

                        <div className="bulkModalInputWrap">
                          <input
                            type="number"
                            className="bulkModalInput"
                            value={bulkForm.prixAbonnement.value}
                            disabled={!bulkForm.prixAbonnement.enabled}
                            onChange={(e) =>
                              updateBulkField("prixAbonnement", {
                                value: e.target.value,
                              })
                            }
                            min={0}
                            placeholder="0"
                          />
                          <span className="bulkModalInputSuffix">Ar</span>
                        </div>
                      </div>

                      {/* Prix Boost */}
                      <div
                        className={`bulkModalField ${bulkForm.prixBoost.enabled ? "isActive" : ""}`}
                      >
                        <label
                          className="bulkModalCheck"
                          htmlFor="chkPrixBoost"
                        >
                          <input
                            type="checkbox"
                            id="chkPrixBoost"
                            checked={bulkForm.prixBoost.enabled}
                            onChange={(e) =>
                              updateBulkField("prixBoost", {
                                enabled: e.target.checked,
                              })
                            }
                          />
                          <span className="bulkModalCheckmark" />
                          <span className="bulkModalFieldLabel">
                            <i className="bi bi-rocket-takeoff" />
                            Prix Boost
                          </span>
                        </label>

                        {valeursActuellesGroupe.prixBoost.mixed ? (
                          <span className="bulkModalCurrentHint mixed">
                            <i className="bi bi-exclamation-triangle" /> Valeurs
                            différentes
                          </span>
                        ) : (
                          <span className="bulkModalCurrentHint">
                            Actuel :{" "}
                            <b>
                              {valeursActuellesGroupe.prixBoost.value ?? 0} Ar
                            </b>
                          </span>
                        )}

                        <div className="bulkModalInputWrap">
                          <input
                            type="number"
                            className="bulkModalInput"
                            value={bulkForm.prixBoost.value}
                            disabled={!bulkForm.prixBoost.enabled}
                            onChange={(e) =>
                              updateBulkField("prixBoost", {
                                value: e.target.value,
                              })
                            }
                            min={0}
                            placeholder="0"
                          />
                          <span className="bulkModalInputSuffix">Ar</span>
                        </div>
                      </div>

                      {/* Premium */}
                      <div
                        className={`bulkModalField ${bulkForm.premium.enabled ? "isActive" : ""}`}
                      >
                        <label className="bulkModalCheck" htmlFor="chkPremium">
                          <input
                            type="checkbox"
                            id="chkPremium"
                            checked={bulkForm.premium.enabled}
                            onChange={(e) =>
                              updateBulkField("premium", {
                                enabled: e.target.checked,
                              })
                            }
                          />
                          <span className="bulkModalCheckmark" />
                          <span className="bulkModalFieldLabel">
                            <i className="bi bi-gem" />
                            Type
                          </span>
                        </label>

                        {valeursActuellesGroupe.premium.mixed ? (
                          <span className="bulkModalCurrentHint mixed">
                            <i className="bi bi-exclamation-triangle" /> Valeurs
                            différentes
                          </span>
                        ) : (
                          <span className="bulkModalCurrentHint">
                            Actuel :{" "}
                            <b>
                              {Number(valeursActuellesGroupe.premium.value) ===
                              1
                                ? "Premium"
                                : "Standard"}
                            </b>
                          </span>
                        )}

                        <select
                          className="bulkModalSelect"
                          value={bulkForm.premium.value}
                          disabled={!bulkForm.premium.enabled}
                          onChange={(e) =>
                            updateBulkField("premium", {
                              value: e.target.value,
                            })
                          }
                        >
                          <option value="1">Premium</option>
                          <option value="2">Standard</option>
                        </select>
                      </div>

                      {/* Has Price */}
                      <div
                        className={`bulkModalField ${bulkForm.hasPrice.enabled ? "isActive" : ""}`}
                      >
                        <label className="bulkModalCheck" htmlFor="chkHasPrice">
                          <input
                            type="checkbox"
                            id="chkHasPrice"
                            checked={bulkForm.hasPrice.enabled}
                            onChange={(e) =>
                              updateBulkField("hasPrice", {
                                enabled: e.target.checked,
                              })
                            }
                          />
                          <span className="bulkModalCheckmark" />
                          <span className="bulkModalFieldLabel">
                            <i className="bi bi-tag" />
                            Nécessite Prix
                          </span>
                        </label>

                        {valeursActuellesGroupe.hasPrice.mixed ? (
                          <span className="bulkModalCurrentHint mixed">
                            <i className="bi bi-exclamation-triangle" /> Valeurs
                            différentes
                          </span>
                        ) : (
                          <span className="bulkModalCurrentHint">
                            Actuel :{" "}
                            <b>
                              {Number(valeursActuellesGroupe.hasPrice.value) ===
                              1
                                ? "Oui"
                                : "Non"}
                            </b>
                          </span>
                        )}

                        <select
                          className="bulkModalSelect"
                          value={bulkForm.hasPrice.value}
                          disabled={!bulkForm.hasPrice.enabled}
                          onChange={(e) =>
                            updateBulkField("hasPrice", {
                              value: e.target.value,
                            })
                          }
                        >
                          <option value="1">Oui</option>
                          <option value="2">Non</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="bulkModalFooter">
                    <button
                      type="button"
                      className="bulkModalBtn bulkModalBtnSoft"
                      onClick={closeBulkModal}
                      disabled={bulkSaving}
                    >
                      Fermer
                    </button>
                    <button
                      type="submit"
                      className="bulkModalBtn bulkModalBtnPrimary"
                      disabled={bulkSaving}
                    >
                      {bulkSaving ? (
                        <>
                          <span className="bulkModalSpinner" />
                          Enregistrement...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check2-circle" />
                          Enregistrer
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
